// Componente refatorado conforme os requisitos melhorados
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useMediaQuery } from "react-responsive";
import PerfectScrollbar from "react-perfect-scrollbar";

import "react-perfect-scrollbar/dist/css/styles.css";
import ChatService from "../../../services/ChatServices";
import MoonLoader from "react-spinners/MoonLoader";
import UserContext from "../../../contexts/UserContext";
import EmojiPickerWrapper from "../EmojiPIcker";
import { AppSettings } from "../../../App";
import { formatPhoneNumber, Hash } from "../../../helpers/Helpers";
import BotControls from "./BotControls";
import NotificationsContext from "../../../contexts/NotificationsContext";
import {
  Avatar,
  BackButton,
  ChatAvatar,
  ChatContainer,
  ChatHeader,
  ChatHeaderDetails,
  ChatName,
  ChatStatus,
  ChatWindow,
  ContainerDisconnectedChat,
  ConversationDetails,
  ConversationItem,
  ConversationList,
  ConversationMeta,
  ConversationName,
  DisconnectedIcon,
  DisconnectedText,
  DisconnectedTitle,
  EmojiButton,
  EmptyChat,
  EmptyChatText,
  EmptyChatTitle,
  EmptyIcon,
  ErrorMessage,
  ErrorMessageContainer,
  LastMessage,
  LoadingMessage,
  LoadingText,
  LoadingWrapper,
  MessageBubble,
  MessageInput,
  MessageMeta,
  MessagesContainer,
  MessageStatus,
  MessageText,
  MessageTime,
  SearchBox,
  SearchIcon,
  SearchInput,
  SendButton,
  Sidebar,
  SidebarHeader,
  StatusDot,
  StatusIndicator,
  StickyInputContainer,
  UnreadBadge,
  UserAvatar,
  WaStoppedOrPaused,
} from "./styles";

const WhatsAppChat = () => {
  const { bottyData } = useContext(UserContext);
  const { unreadCounts, setUnreadCounts } = useContext(NotificationsContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  // ✅ ESTADOS para o sistema de scroll infinito
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollbarRef = useRef();

  // ✅ REF simples para conversation list (substitui o hook useInfiniteScroll)
  const conversationListRef = useRef(null);
  const currentOffsetRef = useRef(0); // ✅ Controle preciso do offset
  const totalCountRef = useRef(0); // ✅ Total de itens disponíveis
  const isLoadingRef = useRef(false); // ✅ Flag para evitar duplicação
  const lastScrollTimeRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    if (scrollbarRef.current) {
      scrollbarRef.current._container.scrollTop =
        scrollbarRef.current._container.scrollHeight;
    }
  }, []);

  // ✅ FUNÇÃO para carregar mais conversas (mantida igual)
  const loadMoreConversations = useCallback(async () => {
    // ✅ Verificações críticas
    if (!bottyData?.uid || isLoadingRef.current || !hasMoreConversations) {
      return;
    }

    // ✅ Verificar limite total
    if (
      totalCountRef.current > 0 &&
      currentOffsetRef.current >= totalCountRef.current
    ) {
      console.log(
        `❌ Reached limit: ${currentOffsetRef.current} >= ${totalCountRef.current}`
      );
      setHasMoreConversations(false);
      return;
    }

    const currentOffset = currentOffsetRef.current;

    isLoadingRef.current = true;
    setIsLoadingConversations(true);

    try {
      const { results, has_more, total_count } = await ChatService.getChats(
        bottyData.uid,
        currentOffset,
        20
      );

      if (typeof total_count === "number") {
        totalCountRef.current = total_count;
      }

      if (results && results.length > 0) {
        const formattedConversations = results.map((chat) => ({
          id: chat.id,
          phone: chat.phone,
          contact: chat?.contact || null,
          name: chat.name || chat.phone,
          avatar: `https://ui-avatars.com/api/?name=${
            chat.name || chat.phone
          }&background=random`,
          label: chat?.label || null,
          lastMessage: chat.message || "",
          lastMessageTime: new Date(chat.timestamp).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
          botStatus: chat.bot_status,
          messages: [],
        }));
        console.log(formattedConversations);
        // ✅ Verificar duplicatas
        setConversations((prev) => {
          const existingIds = new Set(prev.map((conv) => conv.id));
          const newConversations = formattedConversations.filter(
            (conv) => !existingIds.has(conv.id)
          );
          return [...prev, ...newConversations];
        });

        // ✅ Atualizar offset com base nos resultados reais
        currentOffsetRef.current += results.length;
      }

      // ✅ Verificar se tem mais baseado na API e limite
      const shouldHaveMore =
        has_more &&
        (totalCountRef.current === 0 ||
          currentOffsetRef.current < totalCountRef.current);
      setHasMoreConversations(shouldHaveMore);
    } catch (error) {
      console.error("❌ Failed to load more conversations:", error);
      setHasMoreConversations(false);
    } finally {
      isLoadingRef.current = false;
      setIsLoadingConversations(false);
    }
  }, [bottyData?.uid, hasMoreConversations]);

  useEffect(() => {
    const handleConversationScroll = (e) => {
      // ✅ Throttle
      const now = Date.now();
      if (now - lastScrollTimeRef.current < 100) return;
      lastScrollTimeRef.current = now;

      // ✅ Verificações
      if (isLoadingRef.current || !hasMoreConversations) return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const threshold = 150;
      const remainingScroll = scrollHeight - scrollTop - clientHeight;

      if (remainingScroll <= threshold) {
        console.log("🎯 Triggering loadMoreConversations");
        loadMoreConversations();
      }
    };

    const conversationList = conversationListRef.current;
    if (!conversationList) return;

    let scrollElement =
      conversationList.querySelector(".ps-content") ||
      conversationList.querySelector(".ps__rail-y")?.parentElement ||
      conversationList.querySelector(".ps") ||
      conversationList.firstElementChild;

    if (!scrollElement) scrollElement = conversationList;

    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleConversationScroll, {
        passive: true,
      });
      return () =>
        scrollElement.removeEventListener("scroll", handleConversationScroll);
    }
  }, [loadMoreConversations, hasMoreConversations]);

  // Função universal para renderizar texto com quebras de linha
  const renderTextWithLineBreaks = useCallback((textContent) => {
    if (!textContent || typeof textContent !== "string") return null;

    return textContent
      .split("\n")
      .map((line, index) => (
        <div key={`line-${index}`}>{line || "\u00A0"}</div>
      ));
  }, []);

  // Função principal para processar quebras de mensagem
  const processMessageBreaks = useCallback(
    (messageText, baseMessage, sourceType = "api") => {
      if (!messageText || typeof messageText !== "string") {
        return [
          {
            ...baseMessage,
            text: messageText || "",
          },
        ];
      }

      const messages = [];
      let timestampIncrement = 0;

      // Verificar se tem [[break]]
      if (messageText.includes("[[break]]")) {
        // Dividir por [[break]] e filtrar segmentos vazios
        const segments = messageText
          .split("[[break]]")
          .filter((segment) => segment.trim() !== "");

        segments.forEach((segment) => {
          const processedSegments = processSegmentForMedia(segment.trim(), {
            ...baseMessage,
            timestamp:
              typeof baseMessage.timestamp === "number"
                ? baseMessage.timestamp + timestampIncrement
                : Date.now() + timestampIncrement,
          });

          messages.push(...processedSegments);
          timestampIncrement += processedSegments.length;
        });
      } else {
        // Sem breaks, apenas processar para mídia
        const processedSegments = processSegmentForMedia(
          messageText,
          baseMessage
        );
        messages.push(...processedSegments);
      }

      return messages.filter(
        (msg) => msg && msg.text && msg.text.trim() !== ""
      );
    },
    []
  );

  // Função para processar segmento individual para detecção de mídia
  const processSegmentForMedia = useCallback((segmentText, baseMessage) => {
    if (!segmentText || segmentText.trim() === "") {
      console.log("Empty segment, returning empty array");
      return [];
    }

    const messages = [];
    let timestampIncrement = 0;

    // Regex para detectar imagens AWS (escapando corretamente)
    const awsImageRegex =
      /https:\/\/upsoft\.s3\.us-east-1\.amazonaws\.com\/images\/[^\s\n]+/g;
    const imageMatches = [...segmentText.matchAll(awsImageRegex)];

    if (imageMatches.length === 0) {
      console.log("No AWS images, returning normal message");
      // Sem imagens AWS, retorna mensagem normal
      return [
        {
          ...baseMessage,
          text: segmentText,
        },
      ];
    }

    let lastIndex = 0;

    imageMatches.forEach((match, matchIndex) => {
      console.log(`Processing image match ${matchIndex}:`, match[0]);

      // Texto antes da imagem
      if (match.index > lastIndex) {
        const textBefore = segmentText.substring(lastIndex, match.index).trim();
        if (textBefore !== "") {
          console.log("Adding text before image:", textBefore);
          messages.push({
            ...baseMessage,
            text: textBefore,
            timestamp:
              typeof baseMessage.timestamp === "number"
                ? baseMessage.timestamp + timestampIncrement
                : Date.now() + timestampIncrement,
          });
          timestampIncrement++;
        }
      }

      // Imagem como mensagem separada usando formato image:
      const imageMessage = {
        ...baseMessage,
        text: `image:${match[0]}`,
        timestamp:
          typeof baseMessage.timestamp === "number"
            ? baseMessage.timestamp + timestampIncrement
            : Date.now() + timestampIncrement,
      };
      console.log("Adding image message:", imageMessage);
      messages.push(imageMessage);
      timestampIncrement++;

      lastIndex = match.index + match[0].length;
    });

    // Texto após a última imagem
    if (lastIndex < segmentText.length) {
      const textAfter = segmentText.substring(lastIndex).trim();
      if (textAfter !== "") {
        console.log("Adding text after image:", textAfter);
        messages.push({
          ...baseMessage,
          text: textAfter,
          timestamp:
            typeof baseMessage.timestamp === "number"
              ? baseMessage.timestamp + timestampIncrement
              : Date.now() + timestampIncrement,
        });
      }
    }

    return messages;
  }, []);

  // Função para formatar mensagens da API
  const formatApiMessages = useCallback(
    (apiResults, conversationName) => {
      const allFormattedMessages = [];

      apiResults.forEach((msg) => {
        const baseMessage = {
          id: msg.id,
          sender: msg.is_sent ? "Me" : conversationName,
          time: new Date(msg.timestamp).toLocaleTimeString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
          status: msg.status || "delivered",
          isMe: msg.is_sent,
          timestamp: msg.timestamp,
        };

        // CORREÇÃO: Processar quebras para mensagens do bot/agente (is_sent: true)
        // e não processar para mensagens do usuário (is_sent: false)
        if (msg.is_sent) {
          // Mensagem enviada pelo bot/agente - processar quebras e mídia
          const processedMessages = processMessageBreaks(
            msg.message,
            baseMessage,
            "api"
          );
          allFormattedMessages.push(...processedMessages);
        } else {
          // Mensagem enviada pelo usuário - não processar quebras
          allFormattedMessages.push({
            ...baseMessage,
            text: msg.message,
          });
        }
      });

      return allFormattedMessages;
    },
    [processMessageBreaks]
  );

  // Função para processar mensagens do WebSocket
  const processWebSocketMessage = useCallback(
    (newMessage) => {
      const baseTime = new Date(newMessage.timestamp * 1000).toLocaleTimeString(
        "pt-BR",
        {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }
      );

      const baseMessage = {
        sender: newMessage.phone,
        time: baseTime,
        status: "delivered",
        isMe: false,
        botStatus: newMessage?.bot_status || 1,
        timestamp: newMessage.timestamp * 1000,
      };

      // Processar quebras para mensagens recebidas
      return processMessageBreaks(newMessage.message, baseMessage, "websocket");
    },
    [processMessageBreaks]
  );

  // Função refatorada para processar mensagens de agente
  const processAgentMessage = useCallback(
    (messageText, selectedConversation) => {
      if (!messageText) return [];

      const currentTimestamp = Date.now();

      // Primeiro, verifica se é JSON de mídia única (manter compatibilidade original)
      try {
        const parsed = JSON.parse(messageText);
        if (parsed.image?.url || parsed.audio?.url || parsed.document?.url) {
          return [
            {
              text: messageText, // Mantém JSON original para renderMessageContent processar
              sender: "Agente IA",
              time: new Date().toLocaleTimeString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }),
              status: "delivered",
              isMe: true,
              timestamp: currentTimestamp,
            },
          ];
        }
      } catch (e) {
        // Não é JSON válido, continua processamento normal
      }

      // Criar mensagem base
      const baseMessage = {
        sender: "Agente IA",
        time: new Date().toLocaleTimeString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
        status: "delivered",
        isMe: true,
        timestamp: currentTimestamp,
      };

      // Processar quebras e mídia
      return processMessageBreaks(messageText, baseMessage, "agent");
    },
    [processMessageBreaks]
  );

  // Função renderMessageContent corrigida para garantir quebras de linha
  const renderMessageContent = useCallback(
    (text) => {
      if (typeof text !== "string") return null;

      // Função para detectar e renderizar texto com imagens AWS inline
      const renderTextWithInlineImages = (text) => {
        const awsImagePattern =
          /https:\/\/upsoft\.s3\.us-east-1\.amazonaws\.com\/images\/[^\s\n]+/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = awsImagePattern.exec(text)) !== null) {
          // Adiciona o texto antes da URL
          if (match.index > lastIndex) {
            const textBefore = text.substring(lastIndex, match.index).trim();
            if (textBefore) {
              parts.push({
                type: "text",
                content: textBefore,
                key: `text-${parts.length}`,
              });
            }
          }

          // Adiciona a imagem
          parts.push({
            type: "image",
            content: match[0],
            key: `image-${parts.length}`,
          });

          lastIndex = match.index + match[0].length;
        }

        // Adiciona o texto restante após a última URL
        if (lastIndex < text.length) {
          const remainingText = text.substring(lastIndex).trim();
          if (remainingText) {
            parts.push({
              type: "text",
              content: remainingText,
              key: `text-${parts.length}`,
            });
          }
        }

        // Se encontrou partes (texto + imagens), renderiza intercalado
        if (parts.length > 0) {
          return (
            <div>
              {parts.map((part) => {
                if (part.type === "text") {
                  return (
                    <div key={part.key} className="mb-2">
                      {renderTextWithLineBreaks(part.content)}
                    </div>
                  );
                } else if (part.type === "image") {
                  return (
                    <div key={part.key} className="mb-2">
                      <img
                        src={part.content}
                        alt="imagem"
                        style={{ maxWidth: "250px", borderRadius: "12px" }}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }

        return null;
      };

      // Verifica se há URLs de imagem AWS no texto para renderização inline
      // Só executa se NÃO começar com prefixos conhecidos E não for JSON
      if (
        !text.startsWith("image:") &&
        !text.startsWith("audio:") &&
        !text.startsWith("pdf:") &&
        !text.startsWith("{")
      ) {
        const inlineImageContent = renderTextWithInlineImages(text);
        if (inlineImageContent) return inlineImageContent;

        // Se não há imagens inline, renderiza texto normal com quebras de linha
        return <div>{renderTextWithLineBreaks(text)}</div>;
      }

      // Função para tratar prefixos (mantida igual ao original)
      const handlePrefix = (prefix) => {
        const [, urlAndCaption] = text.split(`${prefix}:`);
        if (!urlAndCaption) return null;

        const [url, ...captionParts] = urlAndCaption.trim().split(" ");
        const caption = captionParts.join(" ");

        if (prefix === "image") {
          return (
            <div>
              <img
                src={url}
                alt="imagem"
                style={{ maxWidth: "250px", borderRadius: "12px" }}
              />
              {caption && (
                <div className="mt-1">{renderTextWithLineBreaks(caption)}</div>
              )}
            </div>
          );
        }

        if (prefix === "audio") {
          return (
            <div>
              <audio controls>
                <source src={url} type="audio/ogg" />
                Seu navegador não suporta o elemento de áudio.
              </audio>
              {caption && (
                <div className="mt-1">{renderTextWithLineBreaks(caption)}</div>
              )}
            </div>
          );
        }

        if (prefix === "pdf") {
          return (
            <div>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                download
                style={{ color: "#007bff", fontWeight: "bold" }}
              >
                📄 Baixar documento PDF
              </a>
              {caption && (
                <div className="mt-1">{renderTextWithLineBreaks(caption)}</div>
              )}
            </div>
          );
        }

        return null;
      };

      if (text.startsWith("image:")) return handlePrefix("image");
      if (text.startsWith("audio:")) return handlePrefix("audio");
      if (text.startsWith("pdf:")) return handlePrefix("pdf");

      // Tenta interpretar JSON (mantido igual ao original)
      try {
        const parsed = JSON.parse(text);

        if (parsed.image?.url) {
          const cleanUrl = parsed.image.url.replace(/\\\//g, "/");
          return (
            <div>
              <img
                src={cleanUrl}
                alt="imagem"
                className="mb-2"
                style={{ maxWidth: "200px", borderRadius: "12px" }}
              />
              {parsed.caption && parsed.caption.trim() && (
                <div>{renderTextWithLineBreaks(parsed.caption)}</div>
              )}
            </div>
          );
        }

        if (parsed.audio?.url) {
          const cleanUrl = parsed.audio.url.replace(/\\\//g, "/");
          return (
            <div>
              <audio controls>
                <source src={cleanUrl} type="audio/ogg" />
                Seu navegador não suporta áudio.
              </audio>
              {parsed.caption && parsed.caption.trim() && (
                <div className="mt-1">
                  {renderTextWithLineBreaks(parsed.caption)}
                </div>
              )}
            </div>
          );
        }

        if (parsed.document?.url) {
          const cleanUrl = parsed.document.url.replace(/\\\//g, "/");
          return (
            <div>
              <a
                href={cleanUrl}
                target="_blank"
                rel="noopener noreferrer"
                download={
                  parsed.fileName || parsed.document.fileName || "documento.pdf"
                }
              >
                📄 Baixar documento
              </a>
              {parsed.caption && parsed.caption.trim() && (
                <div className="mt-1">
                  {renderTextWithLineBreaks(parsed.caption)}
                </div>
              )}
            </div>
          );
        }

        // Se chegou aqui, é um JSON que não conseguimos renderizar especificamente
        return <div>{renderTextWithLineBreaks(text)}</div>;
      } catch (e) {
        // Não é JSON válido, renderiza como texto normal COM quebras de linha
        return <div>{renderTextWithLineBreaks(text)}</div>;
      }
    },
    [renderTextWithLineBreaks]
  );

  const loadMoreMessages = useCallback(async () => {
    if (!selectedConversation || isLoading || !hasMoreMessages) return;
    const oldestMessage = selectedConversation.messages[0];
    if (!oldestMessage) return;

    setIsLoading(true);
    try {
      const { results, has_more } = await ChatService.getChatMessages(
        bottyData?.uid,
        selectedConversation.phone,
        oldestMessage.id
      );

      setHasMoreMessages(has_more);

      // Debug log para verificar se as mensagens estão chegando

      // Processar mensagens com a função formatApiMessages
      const allFormattedMessages = formatApiMessages(
        results,
        selectedConversation.name
      );

      setSelectedConversation((prev) => ({
        ...prev,
        messages: [...allFormattedMessages, ...prev.messages],
      }));

      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error("Failed to load more messages:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedConversation,
    isLoading,
    hasMoreMessages,
    bottyData?.uid,
    scrollToBottom,
    formatApiMessages,
  ]);

  useEffect(() => {
    const handleNewMessage = (event) => {
      try {
        // Verifica se é uma mensagem do tipo que queremos
        if (event?.data?.newWaMessage) {
          const newMessage = event.data.newWaMessage;

          // Incrementa o contador se não for a conversa selecionada
          if (selectedConversation?.phone !== newMessage.phone) {
            setUnreadCounts((prev) => ({
              ...prev,
              [newMessage.phone]: (prev[newMessage.phone] || 0) + 1,
            }));
          }

          // Processar mensagem com quebras usando nova função
          const processedMessages = processWebSocketMessage(newMessage);

          // Atualiza a conversa selecionada se for a mesma
          if (selectedConversation?.phone === newMessage.phone) {
            setSelectedConversation((prev) => ({
              ...prev,
              messages: [...prev.messages, ...processedMessages],
            }));
            setTimeout(() => scrollToBottom(), 100);
          }

          // Criar preview para lista de conversas (usar primeira mensagem)
          const firstMessage = processedMessages[0];
          const previewText = firstMessage
            ? firstMessage.text
            : newMessage.message;

          // Atualiza a última mensagem na lista de conversas (código original mantido)
          setConversations((prev) => {
            const exists = prev.some((conv) => conv.phone === newMessage.phone);
            prev = prev.sort((a, b) => {
              const parse = (s) => {
                if (s.includes("/")) {
                  const [d, t] = s.split(", ");
                  const [day, mon] = d.split("/").map(Number);
                  const [h, m] = t.split(":").map(Number);
                  return new Date(new Date().getFullYear(), mon - 1, day, h, m);
                } else {
                  const [h, m] = s.split(":").map(Number);
                  const now = new Date();
                  return new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    h,
                    m
                  );
                }
              };

              return parse(b.lastMessageTime) - parse(a.lastMessageTime);
            });

            if (exists) {
              return prev.map((conv) => {
                if (conv.phone === newMessage.phone) {
                  return {
                    ...conv,
                    lastMessage:
                      previewText.substring(0, 30) +
                      (previewText.length > 30 ? "..." : ""),
                    lastMessageTime: firstMessage ? firstMessage.time : "",
                  };
                }
                return conv;
              });
            } else {
              return [
                ...prev,
                {
                  id: newMessage.phone,
                  phone: newMessage.phone,
                  name: newMessage.phone,
                  avatar: `https://ui-avatars.com/api/?name=${newMessage.phone}&background=random`,
                  lastMessage:
                    previewText.substring(0, 30) +
                    (previewText.length > 30 ? "..." : ""),
                  lastMessageTime: firstMessage ? firstMessage.time : "",
                  messages: processedMessages,
                },
              ];
            }
          });
        }

        if (event?.data?.newWaSentMessage) {
          if (!selectedConversation) return;

          const processedMessages = processAgentMessage(
            event.data.newWaSentMessage,
            selectedConversation
          );

          setSelectedConversation((prev) => ({
            ...prev,
            messages: [...prev.messages, ...processedMessages],
          }));

          const firstMessage = processedMessages[0];
          const previewText = firstMessage
            ? firstMessage.text
            : event.data.newWaSentMessage;

          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === selectedConversation.id
                ? {
                    ...conv,
                    lastMessage:
                      previewText.substring(0, 30) +
                      (previewText.length > 30 ? "..." : ""),
                    lastMessageTime: firstMessage ? firstMessage.time : "",
                  }
                : conv
            )
          );

          setTimeout(() => scrollToBottom(), 100);
        }
      } catch (error) {
        console.error("Error processing new message:", error);
      }
    };

    const handleMessageStatusUpdate = (event) => {
      try {
        // Handle success message (código original mantido)
        if (event?.data?.waMessageSuccess) {
          const { message_id, status } = event.data.waMessageSuccess;

          setSelectedConversation((prev) => {
            if (!prev) return prev;

            return {
              ...prev,
              messages: prev.messages.map((msg) => {
                if (
                  msg.id === message_id ||
                  (msg.timestamp && msg.timestamp.toString() === message_id)
                ) {
                  return {
                    ...msg,
                    status: status === 200 ? "delivered" : "error",
                    errorMessage:
                      status !== 200 ? event.data.message.message : null,
                  };
                }
                return msg;
              }),
            };
          });
        }

        // Handle error message (código original mantido)
        if (event?.data?.waMessageError) {
          const { message: errorMessage } = event.data.waMessageError;

          setSelectedConversation((prev) => {
            if (!prev) return prev;

            const userMessages = prev.messages.filter((msg) => msg.isMe);
            const lastUserMessage = userMessages[userMessages.length - 1];

            if (!lastUserMessage) return prev;

            return {
              ...prev,
              messages: prev.messages.map((msg) => {
                if (
                  msg.id === lastUserMessage.id ||
                  (msg.timestamp && msg.timestamp === lastUserMessage.timestamp)
                ) {
                  return {
                    ...msg,
                    status: "error",
                    errorMessage: errorMessage,
                  };
                }
                return msg;
              }),
            };
          });
        }
      } catch (error) {
        console.error("Error processing message status update:", error);
      }
    };

    window.addEventListener("message", handleNewMessage);
    window.addEventListener("message", handleMessageStatusUpdate);

    return () => {
      window.removeEventListener("message", handleNewMessage);
      window.removeEventListener("message", handleMessageStatusUpdate);
    };
  }, [
    selectedConversation?.phone,
    scrollToBottom,
    setUnreadCounts,
    setConversations,
    setSelectedConversation,
    processWebSocketMessage,
    processAgentMessage,
  ]);

  // ✅ CARREGAMENTO INICIAL (mantido exatamente como estava)
  useEffect(() => {
    const fetchConversations = async () => {
      if (isLoadingRef.current) return;

      setIsLoading(true);
      isLoadingRef.current = true;

      // ✅ Reset dos refs
      currentOffsetRef.current = 0;
      totalCountRef.current = 0;
      setHasMoreConversations(true);

      try {
        const { results, has_more, total_count } = await ChatService.getChats(
          bottyData.uid,
          0,
          20
        );

        if (results && results.length > 0) {
          const formattedConversations = results.map((chat) => ({
            id: chat.id,
            phone: chat.phone,
            contact: chat?.contact || null,
            name: chat.name || chat.phone,
            avatar: `https://ui-avatars.com/api/?name=${
              chat.name || chat.phone
            }&background=random`,
            label: chat?.label || null,
            lastMessage: chat.message || "",
            lastMessageTime: new Date(chat.timestamp).toLocaleString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
            botStatus: chat.bot_status,
            messages: [],
          }));

          setConversations(formattedConversations);

          // ✅ Atualizar refs
          currentOffsetRef.current = results.length;
          if (typeof total_count === "number") {
            totalCountRef.current = total_count;
          }

          if (!isMobile && formattedConversations.length > 0) {
            handleSelectConversation(formattedConversations[0]);
          }
          setTimeout(() => scrollToBottom(), 100);
        }

        // ✅ Configurar has_more baseado na API e limite
        const shouldHaveMore =
          has_more &&
          (totalCountRef.current === 0 ||
            currentOffsetRef.current < totalCountRef.current);
        setHasMoreConversations(shouldHaveMore);
      } catch (error) {
        console.error("❌ Failed to load initial conversations:", error);
        setHasMoreConversations(false);
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    };

    if (bottyData?.uid) {
      fetchConversations();
    }
  }, [bottyData?.uid, isMobile]);

  useEffect(() => {
    if (selectedConversation) {
      const fetchMessages = async () => {
        try {
          const { results } = await ChatService.getChatMessages(
            bottyData?.uid,
            selectedConversation.phone
          );

          // Processar todas as mensagens com a nova função
          const allFormattedMessages = formatApiMessages(
            results,
            selectedConversation.name
          );

          setSelectedConversation((prev) => ({
            ...prev,
            messages: allFormattedMessages,
          }));

          setTimeout(() => scrollToBottom(), 100);

          if (allFormattedMessages.length > 0) {
            const lastMsg =
              allFormattedMessages[allFormattedMessages.length - 1];
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === selectedConversation.id
                  ? {
                      ...conv,
                      lastMessage: lastMsg.text,
                      lastMessageTime: lastMsg.time,
                    }
                  : conv
              )
            );
          }
        } catch (error) {
          console.error("Failed to load messages:", error);
        }
      };
      fetchMessages();
    }
  }, [
    selectedConversation?.id,
    bottyData?.uid,
    scrollToBottom,
    formatApiMessages,
  ]);

  // ✅ SCROLL DE MENSAGENS (mantido exatamente como estava)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleMessagesScroll = () => {
      if (container.scrollTop === 0 && !isLoading) {
        if (bottyData?.uid) {
          loadMoreMessages(); // Esta função já existe para mensagens
        }
      }
    };

    container.addEventListener("scroll", handleMessagesScroll);
    return () => container.removeEventListener("scroll", handleMessagesScroll);
  }, [loadMoreMessages, isLoading, bottyData?.uid]);

  useEffect(() => {
    if (window.innerWidth < 768 && document.getElementById("chat-wrapper")) {
      document.querySelector(".bottom-navigation").style.display = "none";
    }
  }, []);

  const handleEmojiSelect = (unicode) => {
    setMessage((prev) => prev + unicode);
    inputRef.current.focus();
  };

  const filteredConversations = conversations.filter(
    (conv) =>
      conv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.phone.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectConversation = useCallback(
    (conversation) => {
      setSelectedConversation(conversation);
      setHasMoreMessages(true);
      setUnreadCounts((prev) => ({
        ...prev,
        [conversation.phone]: 0,
      }));
      if (isMobile) setShowSidebar(false);
    },
    [isMobile, setUnreadCounts]
  );

  const handleSendMessage = useCallback(async () => {
    if (!message || !message.trim()) return;
    const newMessage = {
      text: message,
      sender: "Me",
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      status: "sent",
      isMe: true,
      timestamp: new Date().toISOString(),
    };
    setSelectedConversation((prev) => ({
      ...prev,
      messages: [...prev.messages, newMessage],
    }));
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === selectedConversation.id
          ? {
              ...conv,
              lastMessage: message,
              lastMessageTime: newMessage.time,
            }
          : conv
      )
    );
    setMessage("");
    setShowEmojiPicker(false);

    const payload = {
      uid: bottyData?.uid,
      unique: bottyData?.unique,
      token: bottyData?.token,
      message: message,
      phone: selectedConversation.phone,
    };
    try {
      await ChatService.sendMessage(payload);
      setSelectedConversation((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "delivered" } : msg
        ),
      }));
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error("Failed to send message:", error);
      setSelectedConversation((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "error" } : msg
        ),
      }));
    }
  }, [
    message,
    selectedConversation,
    bottyData,
    scrollToBottom,
    setConversations,
    setSelectedConversation,
  ]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey && message?.trim()?.length > 0) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, message]
  );

  const renderLastMessagePreview = useCallback((text = "") => {
    const maxLength = 25;

    const getIcon = (type) => {
      switch (type) {
        case "image":
          return "📷";
        case "audio":
          return "🔊";
        case "pdf":
        case "document":
          return "📄";
        default:
          return "";
      }
    };

    // Primeiro, vamos verificar se é um formato JSON
    try {
      const parsed = JSON.parse(text);

      // Determinar o tipo de mídia baseado no conteúdo JSON
      let mediaType = null;
      let caption = "";

      if (parsed.image?.url) {
        mediaType = "image";
        caption = parsed.caption || "";
      } else if (parsed.audio?.url) {
        mediaType = "audio";
        caption = parsed.caption || "";
      } else if (parsed.document?.url) {
        mediaType = "document";
        caption = parsed.caption || "";
      }

      if (mediaType) {
        const previewText = caption
          ? caption.length > maxLength
            ? caption.slice(0, maxLength) + "..."
            : caption
          : `${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}`;

        return `${getIcon(mediaType)} ${previewText}`;
      }

      // Se chegou aqui, é um JSON que não conseguimos interpretar como mídia
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    } catch (e) {
      // Não é JSON, continua com o processamento normal

      // Verifica prefixos
      const prefixes = ["image:", "audio:", "pdf:"];
      const prefix = prefixes.find((p) => text.startsWith(p));

      if (prefix) {
        const type = prefix.replace(":", "");
        const [, urlAndCaption] = text.split(prefix);
        const [url, ...captionParts] = urlAndCaption.trim().split(" ");
        const caption = captionParts.join(" ");

        const previewText = caption
          ? caption.length > maxLength
            ? caption.slice(0, maxLength) + "..."
            : caption
          : "";

        return `${getIcon(type)} ${previewText}`;
      }

      // Caso não tenha prefixo, exibe o texto puro limitado
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    }
  }, []);

  return bottyData?.status === "connected" && bottyData?.receive_chats === 1 ? (
    <ChatContainer id="chat-wrapper">
      {(!isMobile || showSidebar) && (
        <Sidebar>
          <SidebarHeader>
            <UserAvatar
              src={
                AppSettings?.logo
                  ? Hash("250x250", AppSettings.logo)
                  : `https://ui-avatars.com/api/?name=${AppSettings.name}&background=random`
              }
              alt="User"
            />
            <SearchBox>
              <SearchIcon>
                <i className="fa fa-search" />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Busque ou comece uma conversa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
          </SidebarHeader>

          {/* ✅ ConversationList com ref simples e scroll infinito implementado diretamente */}
          <ConversationList ref={conversationListRef}>
            <PerfectScrollbar>
              {isLoading ? (
                <LoadingWrapper>
                  <MoonLoader size={30} color="#25d366" />
                  <LoadingText>Carregando conversas...</LoadingText>
                </LoadingWrapper>
              ) : (
                <>
                  {filteredConversations.map((conversation, index) => (
                    <ConversationItem
                      key={`${conversation.id}-${index}`}
                      active={selectedConversation?.id === conversation.id}
                      onClick={() => handleSelectConversation(conversation)}
                    >
                      <Avatar
                        src={conversation.avatar}
                        alt={conversation.name}
                      />
                      <ConversationDetails>
                        <ConversationName
                          id={`conversation${conversation.phone}`}
                          hasUnread={unreadCounts[conversation.phone] > 0}
                        >
                          {conversation?.name?.startsWith("+")
                            ? formatPhoneNumber(conversation.name)
                            : conversation.name}
                        </ConversationName>
                        <LastMessage>
                          {renderLastMessagePreview(conversation.lastMessage)}
                        </LastMessage>
                      </ConversationDetails>
                      <ConversationMeta>
                        <MessageTime>
                          {conversation.lastMessageTime}
                        </MessageTime>
                        {unreadCounts[conversation.phone] > 0 && (
                          <UnreadBadge>
                            {unreadCounts[conversation.phone] > 9
                              ? "9+"
                              : unreadCounts[conversation.phone]}
                          </UnreadBadge>
                        )}
                      </ConversationMeta>
                    </ConversationItem>
                  ))}

                  {/* ✅ INDICADORES VISUAIS (removido triggerElementRef) */}
                  {isLoadingConversations && (
                    <LoadingWrapper>
                      <MoonLoader size={20} color="#25d366" />
                      <LoadingText>Carregando mais conversas...</LoadingText>
                    </LoadingWrapper>
                  )}
                </>
              )}
            </PerfectScrollbar>
          </ConversationList>
        </Sidebar>
      )}

      {(!isMobile || !showSidebar) && (
        <ChatWindow>
          {selectedConversation ? (
            <>
              <ChatHeader>
                {isMobile && (
                  <BackButton onClick={() => setShowSidebar(true)}>
                    <i className="fa fa-arrow-left" />
                  </BackButton>
                )}
                <ChatAvatar
                  src={selectedConversation.avatar}
                  alt={selectedConversation.name}
                />
                <ChatHeaderDetails>
                  <ChatName id={`chat${selectedConversation.phone}`}>
                    {selectedConversation.name}
                    {selectedConversation.label && (
                      <span
                        className="badge badge-sm ml-2"
                        style={{
                          background: `${selectedConversation.label.color}`,
                        }}
                      >
                        {selectedConversation.label.name}
                      </span>
                    )}
                  </ChatName>
                  <ChatStatus>
                    {selectedConversation.botStatus === 0 ? (
                      <StatusIndicator status="disabled">
                        <StatusDot status="disabled" />
                        Agente Desativado
                      </StatusIndicator>
                    ) : selectedConversation.botStatus === 1 ? (
                      <StatusIndicator status="active">
                        <StatusDot status="active" />
                        Agente Ativo
                      </StatusIndicator>
                    ) : (
                      <StatusIndicator status="paused">
                        <StatusDot status="paused" />
                        Agente Pausado
                      </StatusIndicator>
                    )}
                  </ChatStatus>
                </ChatHeaderDetails>
                <BotControls
                  bottyData={bottyData}
                  contact={selectedConversation.contact}
                  phone={selectedConversation.phone}
                  status={selectedConversation.botStatus || 0}
                  onStatusChange={(newStatus) => {
                    setSelectedConversation((prev) => ({
                      ...prev,
                      botStatus: newStatus,
                    }));
                    setConversations((prev) =>
                      prev.map((conv) =>
                        conv.phone === selectedConversation.phone
                          ? { ...conv, botStatus: newStatus }
                          : conv
                      )
                    );
                  }}
                  setConversations={setConversations}
                  setSelectedConversation={setSelectedConversation}
                />
              </ChatHeader>

              <MessagesContainer ref={messagesContainerRef}>
                <PerfectScrollbar
                  ref={scrollbarRef}
                  className="d-flex flex-column px-2"
                  style={{ height: "calc(100% - 60px)" }}
                >
                  {selectedConversation.messages.map((msg, index) => (
                    <MessageBubble key={index} isMe={msg.isMe}>
                      <MessageText>
                        {renderMessageContent(msg.text)}
                      </MessageText>
                      <MessageMeta>
                        <MessageTime>
                          {msg.time.indexOf(",") > -1
                            ? msg.time.split(",")[1]
                            : msg.time}
                        </MessageTime>
                        {msg.isMe && (
                          <MessageStatus status={msg.status}>
                            {msg.status === "sent"
                              ? "✓"
                              : msg.status === "delivered"
                              ? "✓✓"
                              : msg.status === "error"
                              ? "✗"
                              : "✓"}
                            {msg.status === "error" && (
                              <ErrorMessageContainer>
                                <ErrorMessage>{msg.errorMessage}</ErrorMessage>
                              </ErrorMessageContainer>
                            )}
                          </MessageStatus>
                        )}
                      </MessageMeta>
                    </MessageBubble>
                  ))}
                  {isLoading && (
                    <LoadingMessage>
                      <MoonLoader size={20} color="#25d366" />
                      <span>Carregando mais mensagens...</span>
                    </LoadingMessage>
                  )}
                  <div ref={messagesEndRef} />
                </PerfectScrollbar>
              </MessagesContainer>

              <StickyInputContainer>
                <EmojiButton
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  <i className="fa fa-smile-o" />
                </EmojiButton>

                {showEmojiPicker && (
                  <EmojiPickerWrapper
                    onEmojiSelect={handleEmojiSelect}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                )}

                <MessageInput
                  ref={inputRef}
                  placeholder="Digite uma mensagem..."
                  value={message || ""}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                />

                <SendButton
                  onClick={handleSendMessage}
                  disabled={!message || !message.trim()}
                >
                  <i className="fa fa-paper-plane" />
                </SendButton>
              </StickyInputContainer>
            </>
          ) : (
            <EmptyChat>
              <EmptyIcon>💬</EmptyIcon>
              <EmptyChatTitle>WhatsApp</EmptyChatTitle>
              <EmptyChatText>
                Selecione um chat para começar uma conversa...
              </EmptyChatText>
            </EmptyChat>
          )}
        </ChatWindow>
      )}
    </ChatContainer>
  ) : (
    <ContainerDisconnectedChat>
      {!bottyData?.uid || bottyData?.receive_chats === 1 ? (
        <WaStoppedOrPaused>
          <DisconnectedIcon>📱</DisconnectedIcon>
          <DisconnectedTitle>WhatsApp Desconectado!</DisconnectedTitle>
          <DisconnectedText>
            Conecte seu WhatsApp para visualizar suas conversas.
          </DisconnectedText>
        </WaStoppedOrPaused>
      ) : (
        <WaStoppedOrPaused>
          <DisconnectedIcon>⏸️</DisconnectedIcon>
          <DisconnectedTitle>WhatsApp Pausado!</DisconnectedTitle>
          <DisconnectedText>
            Ative sua conta novamente para visualizar seus chats por aqui.
          </DisconnectedText>
        </WaStoppedOrPaused>
      )}
    </ContainerDisconnectedChat>
  );
};

export default WhatsAppChat;
